import Loader from "atoms/loaders/loader";
import LegacyPublicationPageRecord from "models/view-models/legacy-publication-page-record";
import LegacyPublicationRecord from "models/view-models/legacy-publication-record";
import UnorderedList from "molecules/lists/unordered-list";
import ImageViewer from "organisms/image-viewer/image-viewer";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { siteMap } from "internal-sitemap";
import { usePdfPublicationPageState } from "utilities/atoms/pdf-page-atom";
import { CollectionUtils } from "utilities/collection-utils";
import useTableOfContents from "utilities/hooks/domain/publications/use-table-of-contents";
import useLegacyPublicationSection from "utilities/hooks/domain/publications/legacy/use-legacy-publication-section";
import usePreventRightClick from "utilities/hooks/use-prevent-right-click";
import { t } from "utilities/localization-utils";
import { RouteUtils } from "utilities/route-utils";

interface LegacyPublicationContentProps {
    legacyPublication: LegacyPublicationRecord;
    errors: string[];
    loading: boolean;
    isSidebarOpen: boolean;
}

interface LegacyPublicationRouteParams {
    code: string;
    edition: string;
    pageNumber: string;
    sectionId?: string;
}

const LegacyPublicationContent: React.FC<LegacyPublicationContentProps> = (
    props: LegacyPublicationContentProps
) => {
    // #region Hooks
    const { code, edition, pageNumber, sectionId } =
        useParams<LegacyPublicationRouteParams>();
    const history = useHistory();
    const { legacyPublication, errors, loading, isSidebarOpen } = props;
    const [currentPage, setCurrentPage] =
        useState<LegacyPublicationPageRecord>();
    const firstRenderRef = useRef(false);
    usePreventRightClick();
    const { currentPageNumber, setCurrentPageNumber } =
        usePdfPublicationPageState();
    const { handleActiveIdChange, onFooterPageChange, activeId } =
        useTableOfContents({
            tableOfContentsEntries: legacyPublication.tableOfContents,
            currentPageNumber,
            pageEntries: legacyPublication.legacyPublicationPages,
        });

    const parsedSectionId = sectionId
        ? Number.parseInt(sectionId, 10)
        : undefined;
    const { resultObject: section } = useLegacyPublicationSection({
        sectionId: parsedSectionId,
    });
    // #endregion

    // #region State
    const initialPageNumber = pageNumber ? Number.parseInt(pageNumber, 10) : 1;
    const showPage =
        !CollectionUtils.hasValues(errors) &&
        !loading &&
        currentPage !== undefined;
    const sectionRegions = section?.regions;
    const regionsForCurrentPage = sectionRegions?.filter(
        (region) => region.pageNumber === currentPageNumber
    );
    const polygons = regionsForCurrentPage?.map(
        (region) => region.polygon ?? []
    ) ?? [[]];
    // #endregion

    // #region Effects
    useEffect(() => {
        if (pageNumber && !isNaN(initialPageNumber)) {
            setCurrentPageNumber(initialPageNumber);
        }
    }, [pageNumber, initialPageNumber, setCurrentPageNumber]);

    // Keep the query string in sync when the current page changes.
    useEffect(() => {
        if (!currentPageNumber) return;
        if (!firstRenderRef.current) {
            firstRenderRef.current = true;
            return;
        }

        if (
            pageNumber &&
            !isNaN(initialPageNumber) &&
            initialPageNumber === currentPageNumber
        ) {
            return;
        }

        const route = sectionId
            ? siteMap.publications.permalinks.legacyPublicationSectionPage
            : siteMap.publications.permalinks.legacyPublicationPage;

        history.replace(
            RouteUtils.getUrl(route, {
                code: code,
                edition: edition,
                pageNumber: currentPageNumber,
                sectionId: sectionId,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageNumber]);

    useEffect(() => {
        setCurrentPage(
            legacyPublication.legacyPublicationPages?.find(
                (page) => page.pageNumber === currentPageNumber
            )
        );
    }, [currentPageNumber, legacyPublication.legacyPublicationPages]);

    useEffect(() => () => setCurrentPageNumber(1), [setCurrentPageNumber]);

    useEffect(() => {
        if (
            CollectionUtils.hasValues(legacyPublication.legacyPublicationPages)
        ) {
            const targetPageNumber =
                pageNumber && !isNaN(initialPageNumber) ? initialPageNumber : 1;
            setCurrentPage(
                legacyPublication.legacyPublicationPages.find(
                    (page) => page.pageNumber === targetPageNumber
                )
            );
        }
    }, [legacyPublication, loading, pageNumber, initialPageNumber]);
    // #endregion

    return (
        <React.Fragment>
            {CollectionUtils.hasValues(errors) && (
                <UnorderedList listItems={errors} />
            )}
            {loading && <Loader accessibleText={t("loading")} />}
            {showPage && (
                <div className="c-image-viewer__wrapper">
                    <ImageViewer
                        tableOfContents={legacyPublication.tableOfContents}
                        pages={legacyPublication.legacyPublicationPages ?? []}
                        currentPageNumber={currentPageNumber}
                        polygons={polygons}
                        setCurrentPageNumber={setCurrentPageNumber}
                        activeId={activeId}
                        setActiveId={handleActiveIdChange}
                        onFooterPageChange={onFooterPageChange}
                        isSidebarOpen={isSidebarOpen}
                    />
                </div>
            )}
        </React.Fragment>
    );
};

export default LegacyPublicationContent;
